import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  LinearScale,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';
import { dashboardTokens } from '../../../Dashboard/Components/dashboardCardStyles';
import { DayBucket } from '../metaSelectors';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);

const BAR_TOP = '#a855f7';
const BAR_BOTTOM = '#6d28d9';

interface LeadPerformanceChartProps {
  buckets: DayBucket[];
  loading: boolean;
}

export const LeadPerformanceChart: React.FC<LeadPerformanceChartProps> = ({
  buckets,
  loading,
}) => {
  const hasData = buckets.some((b) => b.count > 0);

  // With sparse data every bar is 1, and a y-axis of 0..1 renders them as
  // full-height slabs that read as broken. Keep at least 4 units of headroom
  // so low counts look like low counts.
  const peak = buckets.reduce((max, b) => Math.max(max, b.count), 0);
  const suggestedMax = Math.max(4, peak + 1);

  const data = useMemo(
    () => ({
      labels: buckets.map((b) => dayjs(b.date).format('MMM D')),
      datasets: [
        {
          label: 'Leads received',
          data: buckets.map((b) => b.count),
          borderRadius: 3,
          maxBarThickness: 18,
          barPercentage: 0.7,
          categoryPercentage: 0.85,
          backgroundColor: (context: { chart: ChartJS }) => {
            const { ctx, chartArea } = context.chart;
            if (!chartArea) return BAR_TOP;
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, BAR_TOP);
            gradient.addColorStop(1, BAR_BOTTOM);
            return gradient;
          },
        },
      ],
    }),
    [buckets],
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1c1c1c',
          borderColor: dashboardTokens.border,
          borderWidth: 1,
          titleColor: '#fff',
          bodyColor: '#cbd5e1',
          padding: 10,
          displayColors: false,
          callbacks: {
            label: (item) => `${item.parsed.y} lead${item.parsed.y === 1 ? '' : 's'}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { color: dashboardTokens.border },
          ticks: {
            color: dashboardTokens.textSecondary,
            font: { size: 11 },
            maxRotation: 0,
            autoSkipPadding: 16,
          },
        },
        y: {
          beginAtZero: true,
          suggestedMax,
          grid: { color: '#242424' },
          border: { display: false },
          ticks: {
            color: dashboardTokens.textSecondary,
            font: { size: 11 },
            precision: 0,
            stepSize: 1,
          },
        },
      },
    }),
    [suggestedMax],
  );

  return (
    <Box
      sx={{
        flex: '2 1 520px',
        minWidth: 320,
        bgcolor: dashboardTokens.cardBg,
        border: `1px solid ${dashboardTokens.border}`,
        borderRadius: dashboardTokens.cardRadius,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>
          Lead Performance
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textSecondary, mt: 0.25 }}>
          Leads received from Meta campaigns
        </Typography>
      </Box>

      <Box sx={{ height: 300, position: 'relative' }}>
        {loading ? (
          <Typography sx={{ fontSize: 13, color: dashboardTokens.textMuted }}>
            Loading lead performance…
          </Typography>
        ) : hasData ? (
          <Bar data={data} options={options} />
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              textAlign: 'center',
              px: 2,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>
              No leads in this date range
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: dashboardTokens.textMuted, maxWidth: 380 }}>
              Leads appear here as soon as Meta delivers them. Widen the date range, or check the
              Meta connection above.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
